// Removal + re-add helpers for the room capture screen.
//
// Tap-past-zero on an asset row opens a confirmation modal; on
// Confirm the row is flipped to confirmed-zero (which the screen
// then hides from the list). Cancel / Esc / overlay close the modal
// and the row stays untouched. Re-add through the AddAssetDialog has
// to dispatch `updateItem` — not `createItem` — when the picked
// asset is one of those hidden rows, otherwise the second create
// would conflict with the existing captured row.
//
// `pendingRemoveAssetTypeIds` tracks rows the user has just removed
// so the room screen can keep them filtered out of `visibleItems`
// even if a delayed `item-changed` event for the row arrives with
// `isZeroConfirmed=false` — e.g., the response to a prior decrement
// dispatch that was still in flight when the surveyor tapped past
// zero. Without this guard the row briefly reappears between the
// optimistic hide and the remove's own server roundtrip.

import { useCallback, useState } from 'react'

import type { CaptureItemRow, SpaceWithItems } from '@citycare/shared'

import type { CaptureClient } from '../client/capture-client.types'
import type { AssetRowPatch } from '../components/asset-row'

export interface PendingRemoveConfirm {
  assetTypeId: string
  assetName: string
}

export interface RoomRemovalApi {
  pendingConfirm: PendingRemoveConfirm | null
  pendingRemoveAssetTypeIds: ReadonlySet<string>
  handleRequestRemove: (assetTypeId: string) => void
  handleConfirmRemove: () => void
  handleCancelRemove: () => void
  // Drops an id from the hidden-rows guard. Called by the room screen
  // when AddAssetDialog re-selects an asset that the surveyor just
  // removed in this same session — without this the row stays hidden
  // by the visibleItems filter even though dispatchAddAssetSelection
  // un-confirmed-zeroed it on the server (Codex P2 on PR #415).
  handleClearPendingRemove: (assetTypeId: string) => void
}

export function useRoomRemoval(
  space: SpaceWithItems | null,
  handleUpdate: (assetTypeId: string, patch: AssetRowPatch) => void,
): RoomRemovalApi {
  const [pendingConfirm, setPendingConfirm] =
    useState<PendingRemoveConfirm | null>(null)
  const [pendingRemoveAssetTypeIds, setPendingRemoveAssetTypeIds] = useState<
    ReadonlySet<string>
  >(() => new Set())

  const handleRequestRemove = useCallback(
    (assetTypeId: string): void => {
      if (space === null) return
      const existing = space.items.find((it) => it.assetTypeId === assetTypeId)
      if (existing === undefined) return
      setPendingConfirm({
        assetTypeId,
        assetName: existing.assetTypeName,
      })
    },
    [space],
  )

  const handleConfirmRemove = useCallback((): void => {
    setPendingConfirm((prev) => {
      if (prev === null) return prev
      const { assetTypeId } = prev
      setPendingRemoveAssetTypeIds((set) => {
        if (set.has(assetTypeId)) return set
        const next = new Set(set)
        next.add(assetTypeId)
        return next
      })
      handleUpdate(assetTypeId, { quantity: '0', isZeroConfirmed: true })
      return null
    })
  }, [handleUpdate])

  const handleCancelRemove = useCallback((): void => {
    setPendingConfirm(null)
  }, [])

  const handleClearPendingRemove = useCallback((assetTypeId: string): void => {
    setPendingRemoveAssetTypeIds((set) => {
      if (!set.has(assetTypeId)) return set
      const next = new Set(set)
      next.delete(assetTypeId)
      return next
    })
  }, [])

  return {
    pendingConfirm,
    pendingRemoveAssetTypeIds,
    handleRequestRemove,
    handleConfirmRemove,
    handleCancelRemove,
    handleClearPendingRemove,
  }
}

// Dispatches the right mutation when the AddAssetDialog selects an
// asset type. If the type is already attached to the space but
// currently hidden (isZeroConfirmed=true on a persisted row), we
// restore it via `updateItem` rather than firing a duplicate
// `createItem` that would conflict with the existing capture row.
export async function dispatchAddAssetSelection(args: {
  client: CaptureClient
  sessionId: string
  spaceId: string
  items: readonly CaptureItemRow[]
  assetTypeId: string
}): Promise<void> {
  const { client, sessionId, spaceId, items, assetTypeId } = args
  const existing = items.find((it) => it.assetTypeId === assetTypeId)
  const isPersistedHidden =
    existing !== undefined &&
    existing.isZeroConfirmed &&
    existing.id !== '' &&
    !existing.isTemplateOnly
  if (isPersistedHidden && existing !== undefined) {
    await client.dispatch({
      kind: 'updateItem',
      itemId: existing.id,
      quantity: existing.quantity,
      isZeroConfirmed: false,
      notes: existing.notes,
      syncVersion: existing.syncVersion,
    })
    return
  }
  await client.dispatch({
    kind: 'createItem',
    sessionId,
    spaceId,
    assetTypeId,
    quantity: '0',
    isZeroConfirmed: false,
    notes: null,
  })
}
